import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Descriptions, Input, Modal, Popconfirm, Table } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { doc, updateDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { COMMUNICATION_TYPES } from "../../../../../constants/CommunicationConstants";
import {
  CONTACT_TYPES,
  contactsEqual,
  createContact,
  getContactDisplay,
  getContactValue,
} from "../../../../../constants/ContactConstants";
import { FIRESTORE_CUSTOMERS_TABLE } from "../../../../../constants/FirestoreConstant";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { showOperationSummary } from "../../../../../utils/operationSummary";

export default function EditCommunicationDetailsModal({ company, users, visible, onClose }) {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [communicationPreferences, setCommunicationPreferences] = useState({});
  const [wildcardContacts, setWildcardContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [tempContactValue, setTempContactValue] = useState("");
  const [defaultCommunicationPreferences, setDefaultCommunicationPreferences] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Create extended user list including wildcard contacts
  const extendedUserList = useMemo(() => {
    if (!users) {
      return [];
    }

    // Create user objects (unchanged)
    const userObjects = users.map((user) => ({
      id: user.id,
      userEmail: user.userEmail,
      user: user.user,
      isWildcard: false,
      isActive: user.isActive,
      contactType: CONTACT_TYPES.USER,
    }));

    // Create wildcard contact objects
    const wildcardObjects = wildcardContacts.map((contact) => ({
      id: `wildcard-${contact.type}-${getContactValue(contact)}`,
      userEmail: getContactDisplay(contact, users),
      user: getContactDisplay(contact, users),
      isWildcard: true,
      isActive: true,
      contactType: contact.type,
      contactData: contact,
    }));

    return [...userObjects, ...wildcardObjects];
  }, [users, wildcardContacts]);

  // Initialize communication preferences and wildcard emails when modal opens
  useEffect(() => {
    if (visible && company) {
      setCommunicationPreferences(company.communicationPreferences || {});

      // Initialize default communication preferences
      const defaultPrefs =
        company.defaultCommunicationPreferences ||
        COMMUNICATION_TYPES.reduce((acc, type) => ({ ...acc, [type.value]: false }), {});
      setDefaultCommunicationPreferences(defaultPrefs);

      // Extract existing wildcard contacts from communication preferences
      if (company.communicationPreferences && users) {
        const allContacts = Object.values(company.communicationPreferences).filter(Array.isArray).flat();
        const existingUserIds = users.map((user) => user.id);

        const wildcardContacts = allContacts.filter(
          (contact) => contact.type !== CONTACT_TYPES.USER || !existingUserIds.includes(contact.id),
        );

        // Remove duplicates
        const uniqueWildcards = wildcardContacts.filter(
          (contact, index, arr) => arr.findIndex((c) => contactsEqual(c, contact)) === index,
        );

        setWildcardContacts(uniqueWildcards);
      }
    } else {
      // Reset state when modal closes
      setWildcardContacts([]);
      setEditingContact(null);
      setTempContactValue("");
      setIsLoading(false);
      setDefaultCommunicationPreferences({});
    }
  }, [visible, company, users]);

  // Handle checkbox change
  const handleCheckboxChange = (record, communicationType, checked) => {
    setCommunicationPreferences((prev) => {
      const updated = { ...prev };
      const currentContacts = updated[communicationType] || [];

      // Create contact object based on record type
      const contact = record.isWildcard ? record.contactData : createContact(CONTACT_TYPES.USER, record.id);

      if (checked) {
        // Add contact if not already present
        const exists = currentContacts.some((c) => contactsEqual(c, contact));
        if (!exists) {
          updated[communicationType] = [...currentContacts, contact];
        }
      } else {
        // Remove contact
        updated[communicationType] = currentContacts.filter((c) => !contactsEqual(c, contact));
      }

      return updated;
    });
  };

  // Check if user is assigned to a communication type
  const isUserAssigned = (record, communicationType) => {
    const contacts = communicationPreferences[communicationType] || [];
    const contact = record.isWildcard ? record.contactData : createContact(CONTACT_TYPES.USER, record.id);

    return contacts.some((c) => contactsEqual(c, contact));
  };

  // Handle default communication preference change
  const handleDefaultPreferenceChange = (communicationType, checked) => {
    setDefaultCommunicationPreferences((prev) => ({
      ...prev,
      [communicationType]: checked,
    }));
  };

  // Add new wildcard email
  const addWildcardEmail = () => {
    const newContact = createContact(CONTACT_TYPES.EMAIL, "");
    setWildcardContacts((prev) => [...prev, newContact]);

    // Apply default communication preferences to the new wildcard contact
    setCommunicationPreferences((prev) => {
      const updated = { ...prev };
      Object.entries(defaultCommunicationPreferences).forEach(([commType, isEnabled]) => {
        if (isEnabled) {
          const currentContacts = updated[commType] || [];
          if (!currentContacts.some((c) => contactsEqual(c, newContact))) {
            updated[commType] = [...currentContacts, newContact];
          }
        }
      });
      return updated;
    });

    setEditingContact(newContact);
    setTempContactValue(getContactValue(newContact));
  };

  // Remove wildcard contact
  const removeWildcardContact = (contactToRemove) => {
    setWildcardContacts((prev) => prev.filter((contact) => !contactsEqual(contact, contactToRemove)));

    // Remove from communication preferences
    setCommunicationPreferences((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((commType) => {
        if (Array.isArray(updated[commType])) {
          updated[commType] = updated[commType].filter((contact) => !contactsEqual(contact, contactToRemove));
        }
      });
      return updated;
    });
  };

  // Start editing contact
  const startEditingContact = (contact) => {
    setEditingContact(contact);
    setTempContactValue(getContactValue(contact));
  };

  // Save contact edit
  const saveContactEdit = () => {
    // Early return if no contact is being edited
    if (!editingContact) {
      setTempContactValue("");
      return;
    }

    if (!tempContactValue || tempContactValue.trim() === "") {
      // Remove wildcard contact if input is empty
      removeWildcardContact(editingContact);
    } else if (tempContactValue !== getContactValue(editingContact)) {
      const newContact = createContact(editingContact.type, tempContactValue);

      // Update wildcard contacts array
      setWildcardContacts((prev) =>
        prev.map((contact) => (contactsEqual(contact, editingContact) ? newContact : contact)),
      );

      // Update communication preferences
      setCommunicationPreferences((prev) => {
        const updated = { ...prev };

        if (getContactValue(editingContact) === "") {
          // Apply default preferences to new contact
          Object.entries(defaultCommunicationPreferences).forEach(([commType, isEnabled]) => {
            if (isEnabled) {
              const currentContacts = updated[commType] || [];
              if (!currentContacts.some((c) => contactsEqual(c, newContact))) {
                updated[commType] = [...currentContacts, newContact];
              }
            }
          });
        } else {
          // Rename existing contact in all communication types
          Object.keys(updated).forEach((commType) => {
            if (Array.isArray(updated[commType])) {
              updated[commType] = updated[commType].map((contact) =>
                contactsEqual(contact, editingContact) ? newContact : contact,
              );
            }
          });
        }

        return updated;
      });
    }

    setEditingContact(null);
    setTempContactValue("");
  };

  // Create table columns
  const columns = [
    {
      title: t("user.email"),
      dataIndex: "userEmail",
      key: "userEmail",
      width: 200,
      fixed: "left",
      render: (email, record) => {
        if (record.isWildcard) {
          // Add visual indicator for contact type

          if (editingContact && contactsEqual(editingContact, record.contactData)) {
            return (
              <Input
                value={tempContactValue}
                onChange={(e) => setTempContactValue(e.target.value)}
                onPressEnter={saveContactEdit}
                onBlur={saveContactEdit}
                autoFocus
                size="small"
                style={{ fontSize: "12px" }}
              />
            );
          } else {
            return (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4 }}>
                <span
                  style={{
                    fontStyle: "italic",
                    opacity: 0.7,
                    cursor: "pointer",
                  }}
                  onClick={() => startEditingContact(record.contactData)}
                >
                  {email}
                </span>
                <Popconfirm
                  title={t("confirm_delete_contact", { contact: email })}
                  onConfirm={() => removeWildcardContact(record.contactData)}
                  okText={t("yes")}
                  cancelText={t("no")}
                >
                  <Button type="text" size="small" icon={<DeleteOutlined style={{ opacity: 0.7 }} />} />
                </Popconfirm>
              </div>
            );
          }
        } else {
          return <span>{email}</span>;
        }
      },
    },
    ...COMMUNICATION_TYPES.map((type) => ({
      title: t(type.label),
      key: type.value,
      width: 120,
      align: "center",
      render: (_, record) => (
        <Checkbox
          checked={isUserAssigned(record, type.value)}
          onChange={(e) => handleCheckboxChange(record, type.value, e.target.checked)}
        />
      ),
    })),
  ];

  const handleSave = () => {
    // Check if every communication type has at least one assigned contact
    const unassignedTypes = COMMUNICATION_TYPES.filter((type) => {
      const assignedContacts = communicationPreferences[type.value] || [];
      return assignedContacts.length === 0;
    });

    if (unassignedTypes.length > 0) {
      // Show warning dialog for unassigned communication types
      Modal.confirm({
        title: t("communication_types_not_assigned"),
        content: (
          <div>
            <p>{t("communication_types_not_assigned_warning")}</p>
            <ul style={{ margin: "0 0 20px 20px", padding: 0 }}>
              {unassignedTypes.map((type, index) => (
                <li key={index}>{t(type.label)}</li>
              ))}
            </ul>
          </div>
        ),
        zIndex: 1100,
        okText: t("proceed_anyway"),
        cancelText: t("cancel"),
        onOk: () => {
          // Proceed with saving
          performSave();
        },
        onCancel: () => {
          // Cancel saving and return to editing
          return;
        },
      });
    } else {
      // All communication types are assigned, proceed with saving
      performSave();
    }
  };

  const performSave = async () => {
    setIsLoading(true);

    // Initialize operation results
    const operationResults = {
      firestore: { attempted: true, success: false, error: null },
    };

    try {
      // Clean up communication preferences by removing empty contacts
      const cleanedCommunicationPreferences = Object.entries(communicationPreferences).reduce(
        (acc, [commType, contacts]) => {
          if (Array.isArray(contacts)) {
            const filteredContacts = contacts.filter((contact) => {
              const value = getContactValue(contact);
              return value && value.trim() !== "";
            });
            if (filteredContacts.length > 0) {
              acc[commType] = filteredContacts;
            }
          }
          return acc;
        },
        {},
      );

      // Prepare the update object
      const updateObj = {
        communicationPreferences: cleanedCommunicationPreferences,
        defaultCommunicationPreferences: defaultCommunicationPreferences,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      };

      // Update Firestore
      await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, company.id), updateObj);

      operationResults.firestore.success = true;
      onClose();
      showOperationSummary(operationResults, t);
    } catch (firestoreError) {
      console.error("Firestore error:", firestoreError);
      operationResults.firestore.error = firestoreError.message;
      showOperationSummary(operationResults, t);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      title={t("edit_communication_preferences_value", { company: !!company ? company.customerName : "" })}
      open={visible}
      onOk={handleSave}
      onCancel={onClose}
      okText={t("save")}
      cancelText={t("close")}
      okButtonProps={{ loading: isLoading }}
      width={900}
      zIndex={1002}
    >
      <Table
        columns={columns}
        dataSource={extendedUserList}
        rowKey="id"
        pagination={false}
        scroll={{ x: 700 }}
        size="small"
        bordered
        style={{ marginTop: 20 }}
      />
      <div style={{ marginTop: 16 }}>
        <Button type="dashed" onClick={addWildcardEmail} icon={<PlusOutlined />} size="small">
          {t("add_email_address")}
        </Button>
      </div>
      <div style={{ marginTop: 24 }}>
        <Descriptions title={t("default_communication_preferences")} bordered size="small" layout="vertical" column={1}>
          <Descriptions.Item label={t("default_communication_preferences_instructions")}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {COMMUNICATION_TYPES.map((type) => (
                <Checkbox
                  key={type.value}
                  checked={defaultCommunicationPreferences[type.value] || false}
                  onChange={(e) => handleDefaultPreferenceChange(type.value, e.target.checked)}
                >
                  {t(type.label)}
                </Checkbox>
              ))}
            </div>
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Modal>
  );
}
